import { MutationResult, useApolloClient, useMutation } from '@apollo/client';
import { ExecutionResult } from 'graphql';

import {
  DeleteTimelineEntryMutation,
  DeleteTimelineEntryMutationVariables,
  MeasurementSystem,
} from '../../../types/__generated__';
import { usePageMeta } from '../usePageMeta';
import mutation from './queries/deleteTimelineEntry.gql';
import { updateActivityTotals } from './updateActivityTotals';

export default function useDeleteTimelineEntry(
  measurementSystem?: MeasurementSystem,
  onCompleted?: (data: DeleteTimelineEntryMutation) => void,
): [
  (id: string) => Promise<ExecutionResult<DeleteTimelineEntryMutation>>,
  MutationResult<DeleteTimelineEntryMutation>,
] {
  const client = useApolloClient();
  const { pageType, pageShortName, isPreview } = usePageMeta();

  const [runMutation, mutationResult] = useMutation<
    DeleteTimelineEntryMutation,
    DeleteTimelineEntryMutationVariables
  >(mutation, {
    update(cache, { data }) {
      const entryId = data && data.deleteTimelineEntry;

      if (!entryId) {
        return;
      }

      // This will remove this specific timeline entry from the cache.
      // However, a reference will still exist inside timeline nodes inside Page.
      // We are filtering by canRead in read and merge to hide the dangling reference
      cache.evict({ id: `TimelineEntry:${entryId}` });

      updateActivityTotals(client, pageType, pageShortName, isPreview);
    },
    context: {
      credentials: 'include',
    },
    onCompleted,
  });

  const deleteTimelineEntry = (id: string) =>
    runMutation({
      variables: { input: { id } },
    });

  return [deleteTimelineEntry, mutationResult];
}
